<template>
  <modal event-tracking-source="sign in popup" :hasClose="true" @close="openModal(false)">
    <div class="px-4 sm:px-10 pt-10 sm:pb-10 login-container">
      <div class="text-3xl font-semibold font-artlist text-business-dark">
        Welcome back
      </div>
      <p class="text-lg hidden sm:block text-business-dark">Sign in to your account</p>
      <div class="flex justify-center mt-5 sm:mt-6">
        <button
          type="button"
          @click="proxyRedirect('facebook')"
          class="px-0 social-btn al-btn al-btn--primary-dark-outline sm:px-8 lg:px-12 mr-2 font-bold">
          <i class="fa fa-facebook mr-2"></i>
          Facebook
        </button>
        <button
          type="button"
          @click="proxyRedirect('google')"
          class="px-0 mr-2 font-bold social-btn al-btn al-btn--primary-dark-outline sm:mr-2 sm:px-8 lg:px-12 ml-2">
          <SvgIcon icon="google-al" class="inline-flex" />
          Google
        </button>
      </div>
      <div class="relative z-10 mt-4 text-sm text-center or sm:mt-6">
        <span class="px-4 bg-primary-beige">or</span>
      </div>
      <p
        class="mt-2 text-center text-red-500"
        v-if="globalError.length > 0"
        v-html="globalError"></p>
      <form @submit.prevent="submitForm">
        <div class="relative mt-4 sm:mt-6">
          <floating-input
            type="text"
            v-model="signInData.email"
            label="Email"
            :showValidIcon="isValidEmail" />
        </div>
        <div class="relative mt-4 sm:mt-6">
          <floating-input
            type="password"
            v-model="signInData.password"
            label="Password"
            :showValidIcon="isValidPassword" />
        </div>
        <div class="mt-6 flex items-center justify-between text-sm text-808080">
          <Checkbox
            label="Keep me logged in"
            class="checkbox font-artlist text-primary-purple"
            :checked="signInData.remember_me"
            @input="signInData.remember_me = $event" />
          <a href="/account/password/remind/" class="underline text-primary-purple opacity-80 forgot-password">
            Forgot password?
          </a>
        </div>
        <div class="mt-6 sm:mt-8">
          <LoaderButton :disabled="isDisableSubmitBtn" :is-loading="isLoading">
            Sign In
          </LoaderButton>
        </div>
        <div class="mt-5 text-sm">
          <span class="text-[rgba(0,1,29,0.50)]">Don't have an account?</span>
          <a
            href="/pricing/"
            class="text-primary-purple hover:text-primary-purple"
            @click.prevent="openSignUpModal('Sign up')"
            >Sign up</a
          >
        </div>
      </form>
    </div>
  </modal>
</template>

<script>
import analyticsEvents from '@/utils/analyticsEvents';
import Modal from './Modal';
import SvgIcon from '@/components/SvgIcon';
import FloatingInput from './Fields/FloatingInput';
import Checkbox from './Fields/checkbox';
import SocialSignOn from '@/mixins/SocialSignOn';
import updateDeviceToken from '../../assets/site/js/lib/update-device-token';
import { identify, eventTracker } from '@/utils/eventTracker';
import LoaderButton from './LoaderButton.vue';

export default {
  components: {
    Modal,
    SvgIcon,
    FloatingInput,
    Checkbox,
    LoaderButton,
  },
  mixins: [SocialSignOn],
  props: {
    socialError: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      signInData: {
        email: '',
        password: '',
        remember_me: true,
        device_token: '',
      },
      isDisableSubmitBtn: false,
      isLoading: false,
      globalError: '',
    };
  },
  computed: {
    isValidEmail() {
      var re = /\S+@\S+\.\S+/;
      return re.test(this.signInData.email);
    },
    isValidPassword() {
      return this.signInData.password.length > 0;
    },
  },
  methods: {
    submitForm() {
      eventTracker(analyticsEvents.clickLogin);
      this.globalError = '';

      // Validation
      if (!this.isValidEmail || !this.isValidPassword) {
        this.globalError = 'Email or password incorrect';
        return;
      }

      this.signInData.device_token = updateDeviceToken.getOrUpdateToken();
      this.isDisableSubmitBtn = true;
      this.isLoading = true;
      axios
        .post('/api/sign-in', this.signInData)
        .then(response => {
          identify(response.data.user_id.toString());
          eventTracker(analyticsEvents.loginSuccess);
          this.isLoading = false;
          if (response.data && response.data.redirect) {
            window.location.href = response.data.redirect;
          }
        })
        .catch(error => {
          this.isDisableSubmitBtn = false;
          this.isLoading = false;
          let reason = '';
          if (typeof error.response.data.message !== 'undefined') {
            reason = this.globalError = error.response.data.message;
          }
          eventTracker(analyticsEvents.loginFailed, { reason });
        });
    },
    openModal(open = true) {
      this.$store.dispatch('setOpenSignInModal', { open, clickSource: 'sign in popup' });
    },

    proxyRedirect(provider) {
      eventTracker(analyticsEvents.clickOnSocialLogin, { provider });
      this.redirectTo(provider, this.signInData.remember_me);
    },
    openSignUpModal(buttonName) {
      this.openModal(false);
      this.$nextTick(() => {
        this.$store.dispatch('setOpenSignUpModal', {
          open: true,
          clickSource: 'sign in popup',
          buttonName,
        });
      });
    },
  },
  watch: {
    socialError(newVal) {
      this.globalError = newVal;
    },
  },
};
</script>

<style scoped>
.font-artlist {
  font-family: 'Artlist Sans';
}
.al-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    padding-left: 45px;
    padding-right: 45px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 15px;
    transition: all 250ms;
    border: none;
}
.al-btn--primary-dark {
  @apply bg-primary-purple text-primary-beige;
}
.al-btn--primary-dark-outline {
  @apply border border-solid border-primary-purple bg-transparent text-primary-purple;
}
.al-btn--primary-dark-outline:hover {
  @apply bg-primary-purple text-primary-beige;
}
.al-btn--primary {
  @apply bg-primary-purple text-primary-beige;
}
.al-btn--primary:hover {
  @apply bg-primary-dark-purple;
}
.login-container {
  height: 100%;
  width: 100%;
  overflow-y: auto;
}
.social-btn {
  width: 155px;
  transition: 0.3s all;
}
.social-btn:hover {
  opacity: 0.9;
}
.or {
  @apply text-primary-purple;
}
.or:before {
    content: '';
    width: 100%;
    display: block;
    height: 1px;
    background-color: rgba(0, 1, 29, 0.20);
    position: absolute;
    top: 50%;
    z-index: -1;
}
.forgot-password:hover {
  @apply opacity-100 text-primary-purple;
}
>>> .checkbox > span {
  margin-right: 10px;
}
.text-808080 {
  color: #808080;
}
.text-\[rgba\(0\,1\,29\,0\.50\)\] {
  color: rgba(0, 1, 29, 0.50);
}
.text-4D4D4D {
  color: #4d4d4d;
}
@screen sm {
  .login-container {
    max-width: 475px;
  }
  .social-btn {
    width: 180px;
  }
}

/* Modal styling */
.modal-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 42px;
  z-index: 999;
  overflow-y: auto;
}
>>> .modal-container {
  position: relative;
  margin: 0;
  padding: 0;
  max-width: 1100px;
  background-color: #fff;
  border-radius: 0;
  transition: all 0.3s ease;
  width: 100%;
  height: 100%;
  margin-top: 6px;
}
>>> .modal-body {
  height: auto;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  @apply bg-primary-beige;
}
>>> .modal-container {
  margin-top: 0;
  padding-top: 6px;
}
>>> .close {
  top: 16px !important;
  right: 24px !important;
}
>>> .close:before,
>>> .close:after {
  background: #828282 !important;
  width: 24px !important;
}
@screen sm {
  >>> .close {
    right: -20px !important;
    top: -20px !important;
  }
  >>> .close:before,
  >>> .close:after {
    background: white !important;
  }
  >>> .modal-container {
    width: auto;
    height: auto;
    border-radius: 12px;
    overflow-y: initial;
    max-height: 100%;
    margin-top: 6px;
    padding-top: 0;
  }
  .modal-mask {
    margin-top: 0;
    background-color: rgba(0, 0, 0, 0.8);
  }
}
@media (orientation: landscape) and (max-height: 480px) {
  .modal-mask {
    z-index: 1000;
  }
  >>> .modal-container {
    height: 100%;
    border-radius: 0;
    margin-top: 0;
  }
  >>> .close {
    top: 0 !important;
    right: -24px !important;
  }
}
</style>
